import { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Register from './pages/Register';
import UpdatePassword from './pages/UpdatePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ForcePasswordReset from './pages/ForcePasswordReset';
import TwoFactorSetup from './pages/TwoFactorSetup';
import Profile from './pages/Profile';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import UniversityAdminDashboard from './pages/UniversityAdminDashboard';
import CollegeAdminDashboard from './pages/CollegeAdminDashboard';
import CollegeAdminDepartments from './pages/CollegeAdminDepartments';
import CollegeUserManagement from './pages/CollegeUserManagement';
import CollegeAdminSubjects from './pages/CollegeAdminSubjects';
import HODDashboard from './pages/HODDashboard';
import HODSubjects from './pages/HODSubjects';
import HODUserManagement from './pages/HODUserManagement';
import ModeratorDashboard from './pages/ModeratorDashboard';
import ExaminerDashboard from './pages/ExaminerDashboard';
import SMEDashboard from './pages/SMEDashboard';
import PaperTemplates from './pages/PaperTemplates';
import ExtractTemplateFromPDF from './pages/ExtractTemplateFromPDF';
import CreateTemplateManual from './pages/CreateTemplateManual';
import ManageTemplates from './pages/ManageTemplates';
import TenantFinalizedPapers from './pages/TenantFinalizedPapers';
import MyExaminers from './pages/MyExaminers';
import PDFLibrary from './pages/PDFLibrary'; // Re-enabled with simple text extraction
import SMESelection from './pages/SMESelection';
import SMEPapers from './pages/SMEPapers';
import ModeratorCategorization from './pages/ModeratorCategorization';
import ModeratorCategorizationView from './pages/ModeratorCategorizationView';
import ModeratorApprovals from './pages/ModeratorApprovals';
import QuestionVariations from './pages/QuestionVariations';
import SMEVariationReview from './pages/SMEVariationReview';
import SubQuestionVariations from './pages/SubQuestionVariations';
import SMESubQuestionReview from './pages/SMESubQuestionReview';
import SuperAdminReports from './pages/SuperAdminReports';
import SuperAdminTenants from './pages/SuperAdminTenants';
import SuperAdminAISettings from './pages/SuperAdminAISettings';
import TenantManagement from './pages/TenantManagement';
import SuperAdminUserManagement from './pages/SuperAdminUserManagement';
import SuperAdminFinalizedPapers from './pages/SuperAdminFinalizedPapers';
import TokenConsumption from './pages/TokenConsumption';
import SuperAdminSettings from './pages/SuperAdminSettings';
import Universities from './pages/Universities';
import Moderators from './pages/Moderators';
import FacultyCertification from './pages/FacultyCertification';
import UniversityPoolExaminer from './pages/UniversityPoolExaminer';
import UniversityPoolSME from './pages/UniversityPoolSME';
import UniversityPoolModerator from './pages/UniversityPoolModerator';
import UniversityPoolCOE from './pages/UniversityPoolCOE';
import UniversityPoolManagement from './pages/UniversityPoolManagement';
import COEDashboard from './pages/COEDashboard';
import COEMyPoolPapers from './pages/COEMyPoolPapers';
import COEManageTemplates from './pages/COEManageTemplates';
import CollegeManagement from './pages/CollegeManagement';
import SubjectManagement from './pages/SubjectManagement';
import UniversityUserManagement from './pages/UniversityUserManagement';
import PrivateRoute from './components/PrivateRoute';
import './App.css';
import { AuthContext } from './context/AuthContext';
import Home from './pages/Home';
import { TenantProvider } from './context/TenantContext';
import usePageTracking from './hooks/usePageTracking';

function MainLayout() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  usePageTracking();

  const authPages = ['/login', '/register', '/', '/forgot-password', '/reset-password', '/force-reset-password'];
  const showSidebar = user && !authPages.includes(location.pathname);

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className={`main-content ${showSidebar ? 'with-sidebar' : ''}`}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/force-reset-password" element={<ForcePasswordReset />} />
          <Route path="/" element={<Home />} />

          <Route path="/2fa-setup" element={
            <PrivateRoute>
              <TwoFactorSetup />
            </PrivateRoute>
          } />

          <Route path="/profile" element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } />

          {/* PDF Library - Simple text extraction, no embeddings */}
          <Route path="/pdf-library" element={
            <PrivateRoute>
              <PDFLibrary />
            </PrivateRoute>
          } />

          <Route path="/super-admin" element={
            <PrivateRoute role="super_admin">
              <SuperAdminDashboard />
            </PrivateRoute>
          } />

          <Route path="/university-admin" element={
            <PrivateRoute role="university_admin">
              <UniversityAdminDashboard />
            </PrivateRoute>
          } />

          {/* University Admin Paper Templates */}
          <Route path="/university-admin/paper-templates" element={
            <PrivateRoute role="university_admin">
              <PaperTemplates />
            </PrivateRoute>
          } />

          {/* University Admin Create Template */}
          {/* Finalized Papers tabs */}
          <Route path="/university-admin/finalized-papers" element={
            <PrivateRoute role="university_admin">
              <TenantFinalizedPapers />
            </PrivateRoute>
          } />
          <Route path="/college-admin/finalized-papers" element={
            <PrivateRoute role="college_admin">
              <TenantFinalizedPapers />
            </PrivateRoute>
          } />
          <Route path="/hod/finalized-papers" element={
            <PrivateRoute role="hod">
              <TenantFinalizedPapers />
            </PrivateRoute>
          } />

          {/* Combined Templates (Create + Extract) for university/college/HOD admins */}
          <Route path="/university-admin/templates" element={
            <PrivateRoute role="university_admin">
              <ManageTemplates />
            </PrivateRoute>
          } />
          <Route path="/college-admin/templates" element={
            <PrivateRoute role="college_admin">
              <ManageTemplates />
            </PrivateRoute>
          } />
          <Route path="/hod/templates" element={
            <PrivateRoute role="hod">
              <ManageTemplates />
            </PrivateRoute>
          } />

          <Route path="/university-admin/create-template" element={
            <PrivateRoute role="university_admin">
              <CreateTemplateManual />
            </PrivateRoute>
          } />

          {/* University Admin Template Extraction */}
          <Route path="/university-admin/extract-template" element={
            <PrivateRoute role="university_admin">
              <ExtractTemplateFromPDF />
            </PrivateRoute>
          } />

          {/* University Admin College Management */}
          <Route path="/university-admin/college-management" element={
            <PrivateRoute role="university_admin">
              <CollegeManagement />
            </PrivateRoute>
          } />

          {/* University Admin Subject Management */}
          <Route path="/university-admin/subject-management" element={
            <PrivateRoute role="university_admin">
              <SubjectManagement />
            </PrivateRoute>
          } />

          {/* University Admin User Management */}
          <Route path="/university-admin/user-management" element={
            <PrivateRoute role="university_admin">
              <UniversityUserManagement />
            </PrivateRoute>
          } />

          {/* COE Dashboard - COE role only */}
          <Route path="/coe-dashboard" element={
            <PrivateRoute role="coe">
              <COEDashboard />
            </PrivateRoute>
          } />

          {/* COE My Pool Papers */}
          <Route path="/coe/my-pool-papers" element={
            <PrivateRoute role="coe">
              <COEMyPoolPapers />
            </PrivateRoute>
          } />

          {/* COE Paper Templates */}
          <Route path="/coe/paper-templates" element={
            <PrivateRoute role="coe">
              <PaperTemplates />
            </PrivateRoute>
          } />

          {/* COE Templates - Unified */}
          <Route path="/coe/templates" element={
            <PrivateRoute role="coe">
              <COEManageTemplates />
            </PrivateRoute>
          } />

          {/* Deprecated - redirect to unified templates */}
          <Route path="/coe/create-template" element={
            <PrivateRoute role="coe">
              <COEManageTemplates />
            </PrivateRoute>
          } />

          <Route path="/coe/extract-template" element={
            <PrivateRoute role="coe">
              <COEManageTemplates />
            </PrivateRoute>
          } />

          <Route path="/college-admin" element={
            <PrivateRoute role="college_admin">
              <CollegeAdminDashboard />
            </PrivateRoute>
          } />

          <Route path="/college-admin/departments" element={
            <PrivateRoute role="college_admin">
              <CollegeAdminDepartments />
            </PrivateRoute>
          } />

          <Route path="/college-admin/subjects" element={
            <PrivateRoute role="college_admin">
              <CollegeAdminSubjects />
            </PrivateRoute>
          } />

          <Route path="/college-admin/create-template" element={
            <PrivateRoute role="college_admin">
              <CreateTemplateManual />
            </PrivateRoute>
          } />

          <Route path="/college-admin/extract-template" element={
            <PrivateRoute role="college_admin">
              <ExtractTemplateFromPDF />
            </PrivateRoute>
          } />

          <Route path="/college-admin/users" element={
            <PrivateRoute role="college_admin">
              <CollegeUserManagement />
            </PrivateRoute>
          } />

          <Route path="/hod" element={
            <PrivateRoute role="hod">
              <HODDashboard />
            </PrivateRoute>
          } />

          <Route path="/hod/subjects" element={
            <PrivateRoute role="hod">
              <HODSubjects />
            </PrivateRoute>
          } />

          <Route path="/hod/create-template" element={
            <PrivateRoute role="hod">
              <CreateTemplateManual />
            </PrivateRoute>
          } />

          <Route path="/hod/extract-template" element={
            <PrivateRoute role="hod">
              <ExtractTemplateFromPDF />
            </PrivateRoute>
          } />

          <Route path="/hod/user-management" element={
            <PrivateRoute role="hod">
              <HODUserManagement />
            </PrivateRoute>
          } />

          <Route path="/super-admin/universities" element={
            <PrivateRoute role="super_admin">
              <Universities />
            </PrivateRoute>
          } />

          <Route path="/super-admin/moderators" element={
            <PrivateRoute role="super_admin">
              <Moderators />
            </PrivateRoute>
          } />

            <Route path="/super-admin/reports" element={
            <PrivateRoute role="super_admin">
              <SuperAdminReports />
            </PrivateRoute>
          }/>

          <Route path="/super-admin/ai-settings" element={<Navigate to="/super-admin/settings" replace />} />

          <Route path="/super-admin/tenants" element={
            <PrivateRoute role="super_admin">
              <SuperAdminTenants />
            </PrivateRoute>
          }/>

          <Route path="/super-admin/ai-settings" element={
            <PrivateRoute role="super_admin">
              <SuperAdminAISettings />
            </PrivateRoute>
          }/>

          <Route path="/tenant-management" element={
            <PrivateRoute role="super_admin">
              <TenantManagement />
            </PrivateRoute>
          }/>

          <Route path="/super-admin/user-management" element={
            <PrivateRoute role="super_admin">
              <SuperAdminUserManagement />
            </PrivateRoute>
          }/>

          <Route path="/super-admin/finalized-papers" element={
            <PrivateRoute role="super_admin">
              <SuperAdminFinalizedPapers />
            </PrivateRoute>
          }/>

          <Route path="/super-admin/token-usage" element={
            <PrivateRoute role="super_admin">
              <TokenConsumption />
            </PrivateRoute>
          }/>

          <Route path="/super-admin/settings" element={
            <PrivateRoute role="super_admin">
              <SuperAdminSettings />
            </PrivateRoute>
          }/>

          {/* Faculty Certification - University Admin only */}
          <Route path="/university-admin/faculty-certification" element={
            <PrivateRoute role="university_admin">
              <FacultyCertification />
            </PrivateRoute>
          } />

          {/* University Pool Routes - Require university certification */}
          <Route path="/university-pool/examiner" element={
            <PrivateRoute role="examiner">
              <UniversityPoolExaminer />
            </PrivateRoute>
          } />

          <Route path="/university-pool/sme" element={
            <PrivateRoute role="subject_matter_expert">
              <UniversityPoolSME />
            </PrivateRoute>
          } />

          <Route path="/university-pool/moderator" element={
            <PrivateRoute role="moderator">
              <UniversityPoolModerator />
            </PrivateRoute>
          } />

          {/* University Pool COE - Super Admin, University Admin, or COE */}
          <Route path="/university-pool/coe" element={
            <PrivateRoute role={["super_admin", "university_admin", "coe"]}>
              <UniversityPoolCOE />
            </PrivateRoute>
          } />

          {/* University Pool Management - University Admin or COE */}
          <Route path="/university-pool-management" element={
            <PrivateRoute role={["university_admin", "coe"]}>
              <UniversityPoolManagement />
            </PrivateRoute>
          } />

          <Route path="/moderator" element={
            <PrivateRoute role="moderator">
              <ModeratorDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner" element={
            <PrivateRoute role="examiner">
              <ExaminerDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/create" element={
            <PrivateRoute role="examiner">
              <ExaminerDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/papers" element={
            <PrivateRoute role="examiner">
              <ExaminerDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/view/:paperId" element={
            <PrivateRoute role="examiner">
              <ExaminerDashboard/>
            </PrivateRoute>
          } />

          <Route path="/examiner/university-pool-generate" element={
            <PrivateRoute role="examiner">
              <ExaminerDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/university-pool-view/:poolPaperId" element={
            <PrivateRoute role="examiner">
              <ExaminerDashboard />
            </PrivateRoute>
          } />

          <Route path="/examiner/variations/:paper_id" element={
            <PrivateRoute role="examiner">
              <QuestionVariations />
            </PrivateRoute>
          } />

          <Route path="/examiner/papers/:paper_id/sub-questions" element={
            <PrivateRoute role="examiner">
              <SubQuestionVariations />
            </PrivateRoute>
          } />

          <Route path="/paper-templates" element={
            <PrivateRoute role="examiner">
              <PaperTemplates />
            </PrivateRoute>
          } />

          <Route path="/extract-template" element={
            <PrivateRoute role="examiner">
              <ExtractTemplateFromPDF />
            </PrivateRoute>
          } />

          <Route path="/subject-matter-expert" element={
            <PrivateRoute role="subject_matter_expert">
              <SMEDashboard />
            </PrivateRoute>
          } />

          <Route path="/sme-selection" element={
            <PrivateRoute role="subject_matter_expert">
              <SMESelection />
            </PrivateRoute>
          } />

          <Route path="/sme-papers" element={
            <PrivateRoute role="subject_matter_expert">
              <SMEPapers />
            </PrivateRoute>
          } />

          <Route path="/sme-papers/view/:paperId" element={
            <PrivateRoute role="subject_matter_expert">
              <SMEPapers />
            </PrivateRoute>
          } />

          <Route path="/sme/variation-reviews" element={
            <PrivateRoute role="subject_matter_expert">
              <SMEVariationReview />
            </PrivateRoute>
          } />

          <Route path="/sme/sub-question-reviews" element={
            <PrivateRoute role="subject_matter_expert">
              <SMESubQuestionReview />
            </PrivateRoute>
          } />

          <Route path="/moderator-categorization" element={
            <PrivateRoute role="moderator">
              <ModeratorCategorization />
            </PrivateRoute>
          } />

          <Route path="/moderator-categorization/view/:paperId" element={
            <PrivateRoute role="moderator">
              <ModeratorCategorization />
            </PrivateRoute>
          } />

          <Route path="/moderator-categorization/view/:paperId/category/:categoryKey" element={
            <PrivateRoute role="moderator">
              <ModeratorCategorizationView />
            </PrivateRoute>
          } />

          <Route path="/moderator/users" element={
            <PrivateRoute role="moderator">
              <CollegeUserManagement />
            </PrivateRoute>
          } />

          <Route path="/moderator/approvals" element={
            <PrivateRoute role="moderator">
              <ModeratorApprovals />
            </PrivateRoute>
          } />

          <Route path="/my-examiners" element={
            <PrivateRoute role="subject_matter_expert">
              <MyExaminers />
            </PrivateRoute>
          } />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <TenantProvider>
          <AuthProvider>
            <Router>
              <div className="app">
                <MainLayout />
              </div>
            </Router>
          </AuthProvider>
        </TenantProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
